import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldAlert, Search } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { api, errMsg, fmtDate } from "@/lib/api";

const TONE: Record<string, string> = {
  Received: "bg-[#FFF1CC] text-[#9A5B00]",
  "In progress": "bg-[#FFE4D6] text-[#C2410C]",
  Complete: "bg-[#D5F5EE] text-[#0F766E]",
};

export default function OrgStatus() {
  const { token } = useParams();
  const [d, setD] = useState<any>(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => { api.get(`/public/org-status/${token}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e))); }, [token]);
  const rows = (d?.cases || []).filter((c: any) => !q || `${c.name} ${c.location}`.toLowerCase().includes(q.toLowerCase()));
  const count = (s: string) => (d?.cases || []).filter((c: any) => c.status === s).length;
  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        {error ? (
          <div className="card-soft flex items-start gap-3 p-6 text-[#991B1B]" data-testid="org-status-error"><ShieldAlert className="h-5 w-5 shrink-0" /> {error}</div>
        ) : !d ? (
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#F26B4F]" />
        ) : (
          <>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#E8573A]">Background check status</p>
            <h1 className="mt-1 text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="org-status-name">{d.organization}</h1>
            <div className="mt-6 grid grid-cols-3 gap-3 sm:max-w-xl">
              {["Received", "In progress", "Complete"].map((s) => (
                <div key={s} className={`rounded-2xl px-4 py-3 ${TONE[s]}`} data-testid={`org-status-count-${s.replace(" ", "-").toLowerCase()}`}>
                  <p className="text-2xl font-extrabold">{count(s)}</p><p className="text-sm font-semibold">{s}</p>
                </div>
              ))}
            </div>
            <div className="relative mt-8 max-w-sm">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-[#94A3B8]" />
              <input className="field-input pl-9" placeholder="Search by volunteer name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="org-status-search" />
            </div>
            <div className="card-soft mt-4 overflow-x-auto">
              <table className="tbl" data-testid="org-status-table">
                <thead><tr><th>Volunteer</th>{d.cases.some((c: any) => c.location) && <th>Parish / school</th>}<th>Submitted</th><th>Status</th><th>Completed</th></tr></thead>
                <tbody>
                  {rows.map((c: any, i: number) => (
                    <tr key={i}>
                      <td className="font-semibold">{c.name || "-"}</td>
                      {d.cases.some((x: any) => x.location) && <td className="text-sm">{c.location || "-"}</td>}
                      <td className="text-sm">{fmtDate(c.submitted_at)}</td>
                      <td><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONE[c.status]}`}>{c.status}</span></td>
                      <td className="text-sm">{c.completed_at ? fmtDate(c.completed_at) : "-"}</td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={5} className="py-8 text-center text-[#64748B]">No applications yet.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-[#64748B]">Completed reports are delivered to your organization separately. Keep this link private: anyone with it can see this list.</p>
          </>
        )}
      </div>
    </PublicShell>
  );
}
